import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { mapResponse } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { EffectHandlerService } from '@services/effect-handler.service';
import { ApiService } from '@state/api.service';
import {
  clearAllState,
  deleteOTPDevice,
  deleteOTPDeviceFailure,
  deleteOTPDeviceSuccess,
  getOrCreateOTPDevice,
  getOrCreateOTPDeviceFailure,
  getOrCreateOTPDeviceSuccess,
  refreshAccessToken,
  refreshAccessTokenFailure,
  refreshAccessTokenSuccess,
  retryUnauthorizedAction,
  revokeAccessToken,
  verifyOTPDevice,
  verifyOTPDeviceFailure,
  verifyOTPDeviceSuccess,
} from '@state/authentication/authentication.actions';
import { OTPDevice, TokenResponse } from '@state/authentication/authentication.interfaces';
import { selectRefreshToken } from '@state/authentication/authentication.selectors';
import { AuthenticationService } from '@state/authentication/authentication.service';
import { delay, map, switchMap, tap } from 'rxjs/operators';


@Injectable()
export class AuthenticationEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private service = inject(AuthenticationService);
  private handler = inject(EffectHandlerService);
  private api = inject(ApiService);

  refreshToken = this.store.selectSignal(selectRefreshToken);

  retryUnauthorisedAction$ = createEffect(() => this.actions$.pipe(
    ofType(retryUnauthorizedAction),
    delay(2000),
    tap(({ action }) => this.refreshToken() ? this.store.dispatch(action) : null)
  ), { dispatch: false });

  refreshToken$ = createEffect(() => this.actions$.pipe(
    ofType(refreshAccessToken, retryUnauthorizedAction),
    switchMap(() => this.service.refreshLogin().pipe(
      mapResponse({
        next: (tokenResponse: TokenResponse) => refreshAccessTokenSuccess(tokenResponse),
        error: () => refreshAccessTokenFailure()
      })
    ))
  ));

  refreshTokenError$ = createEffect(() => this.actions$.pipe(
    ofType(refreshAccessTokenFailure),
    map(() => {
      console.error('Token refresh failed. Forced Logout.');
      return clearAllState();
    })
  ));

  revokeToken$ = createEffect(() => this.actions$.pipe(
    ofType(revokeAccessToken),
    switchMap(() => this.service.logout().pipe(
      mapResponse({
        next: () => clearAllState(),
        error: () => clearAllState(),
      })
    )),
  ));

  getOrCreateOTPDevice$ = createEffect(() => this.actions$.pipe(
    ofType(getOrCreateOTPDevice),
    switchMap(({ getOrCreate }) => this.api.otpRetrieve(getOrCreate).pipe(
      this.handler.effectResponse({
        retry: getOrCreateOTPDevice(getOrCreate),
        success: (device: OTPDevice) => getOrCreateOTPDeviceSuccess(device),
        failure: getOrCreateOTPDeviceFailure(),
        failureMessage: 'Error Retrieving OTP Device',
      })
    ))
  ));

  verifyOTPDevice$ = createEffect(() => this.actions$.pipe(
    ofType(verifyOTPDevice),
    switchMap(({ code }) => this.api.otpVerify(code).pipe(
      this.handler.effectResponse({
        success: (device: OTPDevice) => verifyOTPDeviceSuccess(device),
        retry: verifyOTPDevice(code),
        failure: verifyOTPDeviceFailure(),
        failureMessage: 'Error Verifying OTP Device',
        showErrorDetail: true
      }),
    ))
  ));

  deleteOTPDevice$ = createEffect(() => this.actions$.pipe(
    ofType(deleteOTPDevice),
    switchMap(({ device }) => this.api.otpDelete(device).pipe(
      this.handler.effectResponse({
        success: () => deleteOTPDeviceSuccess(),
        retry: deleteOTPDevice(device),
        failure: deleteOTPDeviceFailure(),
        failureMessage: 'Error Deleting OTP Device',
      })
    ))
  ));
}
